/**
 * Sortierung der Kanalliste (Drag & Drop und Tastatur) → Body für `PATCH /servers/:id/channels/positions`.
 * Reihenfolge wie in der Anzeige (`groupServerChannels`): nach Position, Sprachkanäle zuletzt.
 */
import { compareSnowflakes, isDm, isThread, type ChannelDto } from '@lagerfeuer/shared';

export interface PositionUpdate {
  id: string;
  position: number;
  parentId?: string | null;
  lockPermissions?: boolean;
}

const byPosition = (a: ChannelDto, b: ChannelDto) => a.position - b.position || compareSnowflakes(a.id, b.id);
const voiceLast = (a: ChannelDto, b: ChannelDto) => Number(a.type === 'VOICE') - Number(b.type === 'VOICE');

function serverChannels(channels: Record<string, ChannelDto>, serverId: string): ChannelDto[] {
  return Object.values(channels).filter((c) => c.serverId === serverId && !isThread(c.type) && !isDm(c.type));
}

export function categoriesOf(channels: Record<string, ChannelDto>, serverId: string): ChannelDto[] {
  return serverChannels(channels, serverId)
    .filter((c) => c.type === 'CATEGORY')
    .sort(byPosition);
}

/** Effektive Kategorie eines Kanals (unbekannte/gelöschte Kategorie → null, wie in der Anzeige). */
export function parentOf(channels: Record<string, ChannelDto>, ch: ChannelDto): string | null {
  return ch.parentId && channels[ch.parentId]?.type === 'CATEGORY' ? ch.parentId : null;
}

/** Kanäle einer Kategorie (bzw. ohne Kategorie) in Anzeige-Reihenfolge. */
export function siblingsOf(channels: Record<string, ChannelDto>, serverId: string, parentId: string | null): ChannelDto[] {
  return serverChannels(channels, serverId)
    .filter((c) => c.type !== 'CATEGORY' && parentOf(channels, c) === parentId)
    .sort(byPosition)
    .sort(voiceLast);
}

/**
 * Kanal/Kategorie `dragId` an Index `index` (in der Liste ohne ihn selbst) unter `parentId` einsortieren.
 * Kategorien ignorieren `parentId`. Liefert alle neu nummerierten Einträge der Zielliste.
 */
export function planMove(channels: Record<string, ChannelDto>, serverId: string, dragId: string, parentId: string | null, index: number): PositionUpdate[] {
  const drag = channels[dragId];
  if (!drag) return [];
  if (drag.type === 'CATEGORY') {
    const list = categoriesOf(channels, serverId).filter((c) => c.id !== dragId);
    list.splice(clamp(index, list.length), 0, drag);
    return list.map((c, i) => ({ id: c.id, position: i }));
  }
  const list = siblingsOf(channels, serverId, parentId).filter((c) => c.id !== dragId);
  list.splice(clamp(index, list.length), 0, drag);
  const moved = parentOf(channels, drag) !== parentId;
  return list.map((c, i) => {
    const u: PositionUpdate = { id: c.id, position: i, parentId };
    if (c.id === dragId && moved && drag.permissionsSynced && parentId) u.lockPermissions = true;
    return u;
  });
}

/** Drop auf eine Zeile: vor bzw. nach `targetId` einsortieren. */
export function planDrop(channels: Record<string, ChannelDto>, serverId: string, dragId: string, targetId: string, after: boolean): PositionUpdate[] {
  const drag = channels[dragId];
  const target = channels[targetId];
  if (!drag || !target || dragId === targetId) return [];
  if (drag.type === 'CATEGORY') {
    // Kategorien nur relativ zu Kategorien (Ziel-Kanal → seine Kategorie).
    const catId = target.type === 'CATEGORY' ? target.id : parentOf(channels, target);
    if (!catId || catId === dragId) return [];
    const list = categoriesOf(channels, serverId).filter((c) => c.id !== dragId);
    const i = list.findIndex((c) => c.id === catId);
    return planMove(channels, serverId, dragId, null, i + (after || target.type !== 'CATEGORY' ? 1 : 0));
  }
  if (target.type === 'CATEGORY') return planMove(channels, serverId, dragId, target.id, 0);
  const parentId = parentOf(channels, target);
  const list = siblingsOf(channels, serverId, parentId).filter((c) => c.id !== dragId);
  const i = list.findIndex((c) => c.id === targetId);
  return planMove(channels, serverId, dragId, parentId, i + (after ? 1 : 0));
}

/**
 * Tastatur: eins nach oben/unten. Kanäle bleiben in ihrer Gruppe (Text bzw. Sprache), Kategorien unter Kategorien.
 * Liefert null, wenn es in diese Richtung nicht weitergeht.
 */
export function planStep(channels: Record<string, ChannelDto>, serverId: string, id: string, dir: -1 | 1): PositionUpdate[] | null {
  const ch = channels[id];
  if (!ch) return null;
  if (ch.type === 'CATEGORY') {
    const list = categoriesOf(channels, serverId);
    const i = list.findIndex((c) => c.id === id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return null;
    return planMove(channels, serverId, id, null, j);
  }
  const parentId = parentOf(channels, ch);
  const list = siblingsOf(channels, serverId, parentId);
  const i = list.findIndex((c) => c.id === id);
  const j = i + dir;
  const neighbour = list[j];
  if (!neighbour || (neighbour.type === 'VOICE') !== (ch.type === 'VOICE')) return null;
  return planMove(channels, serverId, id, parentId, j);
}

/** Positionen lokal anwenden (vor der API-Antwort). */
export function applyPositions(channels: Record<string, ChannelDto>, body: PositionUpdate[]): void {
  for (const b of body) {
    const c = channels[b.id];
    if (!c) continue;
    c.position = b.position;
    if (b.parentId !== undefined) c.parentId = b.parentId;
    if (b.lockPermissions) c.permissionsSynced = true;
  }
}

function clamp(i: number, len: number): number {
  return Math.max(0, Math.min(i, len));
}
